import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputRightAddon,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DownloadSimple, FileText, Sparkle, Trash, UploadSimple } from 'phosphor-react';
import { toast } from 'sonner';
import { useKanban } from '../../../../hooks/useKanban';

// Tamanho legível (B/KB/MB) — helper local pra não acoplar ao KanbanCardModal.
const formatTamanho = (bytes: number | null): string => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Slug p/ o nome padronizado: sem acento, só A-Z0-9 e '-'.
const slug = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toUpperCase();

const dataHoje = (): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// Separa "recibo.pdf" → { base: 'recibo', ext: '.pdf' }.
const separarNome = (nome: string): { base: string; ext: string } => {
  const dot = nome.lastIndexOf('.');
  if (dot <= 0) return { base: nome, ext: '' };
  return { base: nome.slice(0, dot), ext: nome.slice(dot) };
};

interface CardAnexosProps {
  cardId: number;
  /** Contexto p/ sugerir o nome padrão (EMPRESA_ETAPA_DATA). */
  empresaNome?: string | null;
  etapaNome?: string | null;
}

/**
 * Anexos do card (card-level): reusa o motor de kanban (`/kanban/cards/{id}/anexos/`,
 * upload S3 + listar + deletar). Ficam com o card por TODAS as etapas. Ao anexar, o
 * operador CONFIRMA/edita o nome antes de salvar (extensão travada) — evita bagunça de
 * arquivos; botão "Sugerir padrão" preenche EMPRESA_ETAPA_DATA. Sem backend novo.
 */
export const CardAnexos: React.FC<CardAnexosProps> = ({ cardId, empresaNome, etapaNome }) => {
  const { getAnexos, uploadAnexo, deleteAnexo } = useKanban();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  // Arquivo escolhido aguardando confirmação do nome (fluxo: insere → vê/edita → salva).
  const [pendente, setPendente] = useState<File | null>(null);
  const [baseName, setBaseName] = useState('');
  const [ext, setExt] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['kanban-anexos', cardId],
    queryFn: () => getAnexos(cardId),
    staleTime: 30_000,
  });
  const anexos = data?.data ?? [];

  // Anexar e excluir também viram linha na aba Atividade.
  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['kanban-anexos', cardId] });
    queryClient.invalidateQueries({ queryKey: ['kanban-atividades', cardId] });
  };

  const limparPendente = () => {
    setPendente(null);
    setBaseName('');
    setExt('');
  };

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadAnexo(cardId, file),
    onSuccess: () => {
      toast.success('Anexo enviado');
      invalidar();
      limparPendente();
    },
    onError: () => toast.error('Não foi possível anexar o arquivo.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (anexoId: number) => deleteAnexo(anexoId),
    onSuccess: () => {
      toast.success('Anexo removido');
      invalidar();
    },
    onError: () => toast.error('Não foi possível remover o anexo.'),
  });

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite reescolher o mesmo arquivo
    if (!file) return;
    const { base, ext: extArq } = separarNome(file.name);
    setPendente(file);
    setExt(extArq);
    setBaseName(base); // começa com o nome atual; o operador edita
  };

  const sugerirPadrao = () => {
    const partes = [empresaNome && slug(empresaNome), etapaNome && slug(etapaNome), dataHoje()];
    setBaseName(partes.filter(Boolean).join('_'));
  };

  const confirmar = () => {
    if (!pendente) return;
    const nomeFinal = `${baseName.trim() || 'arquivo'}${ext}`;
    const renomeado = new File([pendente], nomeFinal, { type: pendente.type });
    uploadMutation.mutate(renomeado);
  };

  return (
    <Box>
      <input ref={inputRef} type="file" hidden onChange={onPick} />

      {/* Confirmação/edição do nome antes de salvar (extensão travada). */}
      {pendente ? (
        <Box borderWidth="1px" borderColor="purple.200" bg="purple.50" borderRadius="lg" p={3}>
          <Text fontSize="11px" color="purple.700" mb={2}>
            Confirme o nome do arquivo antes de salvar (evita bagunça nos anexos).
          </Text>
          <InputGroup size="sm">
            <Input
              value={baseName}
              onChange={e => setBaseName(e.target.value)}
              placeholder="nome-do-arquivo"
              bg="white"
              borderRadius="lg"
              autoFocus
            />
            {ext && <InputRightAddon borderRadius="lg">{ext}</InputRightAddon>}
          </InputGroup>
          <Flex align="center" justify="space-between" gap={2} mt={2} wrap="wrap">
            <Button
              size="xs"
              fontSize="13px"
              variant="ghost"
              colorScheme="purple"
              borderRadius="full"
              leftIcon={<Sparkle size={13} weight="fill" />}
              onClick={sugerirPadrao}
            >
              Sugerir padrão
            </Button>
            <Flex gap={2}>
              <Button
                size="xs"
                fontSize="13px"
                variant="ghost"
                borderRadius="full"
                onClick={limparPendente}
                isDisabled={uploadMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                size="xs"
                fontSize="13px"
                colorScheme="purple"
                borderRadius="full"
                px={4}
                onClick={confirmar}
                isLoading={uploadMutation.isPending}
                isDisabled={!baseName.trim()}
              >
                Salvar anexo
              </Button>
            </Flex>
          </Flex>
        </Box>
      ) : (
        <Button
          onClick={() => inputRef.current?.click()}
          variant="outline"
          size="sm"
          fontSize="13px"
          borderRadius="full"
          borderStyle="dashed"
          w="full"
          leftIcon={<UploadSimple size={15} weight="bold" />}
          color="gray.500"
          borderColor="gray.300"
          _hover={{ color: '#7C3AED', borderColor: '#7C3AED', bg: 'white' }}
        >
          Anexar arquivo
        </Button>
      )}

      {isLoading && (
        <Flex justify="center" py={3}>
          <Spinner size="sm" color="purple.400" />
        </Flex>
      )}

      {!isLoading && anexos.length === 0 && !pendente && (
        <Text fontSize="11px" color="gray.400" fontStyle="italic" mt={2} textAlign="center">
          Nenhum anexo ainda.
        </Text>
      )}

      {anexos.map(anexo => (
        <Flex
          key={anexo.id}
          align="center"
          gap={2}
          mt={2}
          p={2}
          borderWidth="1px"
          borderColor="gray.100"
          borderRadius="lg"
          bg="white"
        >
          <FileText size={16} color="#7C3AED" weight="duotone" />
          <Box flex={1} minW={0}>
            <Text fontSize="13px" color="gray.700" fontWeight="500" noOfLines={1}>
              {anexo.nome}
            </Text>
            <Text fontSize="11px" color="gray.400" noOfLines={1}>
              {[formatTamanho(anexo.tamanho), anexo.usuario_nome].filter(Boolean).join(' · ')}
            </Text>
          </Box>
          <Button
            size="xs"
            fontSize="13px"
            variant="ghost"
            borderRadius="full"
            aria-label="Baixar"
            onClick={() => window.open(anexo.url, '_blank')}
          >
            <DownloadSimple size={15} />
          </Button>
          <Button
            size="xs"
            fontSize="13px"
            variant="ghost"
            colorScheme="red"
            borderRadius="full"
            aria-label="Remover"
            isLoading={deleteMutation.isPending && deleteMutation.variables === anexo.id}
            onClick={() => {
              // eslint-disable-next-line no-alert
              if (window.confirm(`Remover "${anexo.nome}"?`)) deleteMutation.mutate(anexo.id);
            }}
          >
            <Trash size={15} />
          </Button>
        </Flex>
      ))}
    </Box>
  );
};

export default CardAnexos;
